import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { validatePasswordStrength, isPasswordValid, PASSWORD_CHECKS, type PasswordValidation } from '@neural-intelligence/shared';
import { setPasswordWithToken } from '../api';

export default function SetPassword() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [validation, setValidation] = useState<PasswordValidation>({
    length: false, uppercase: false, lowercase: false, number: false, special: false,
  });
  const allValid = isPasswordValid(validation);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!allValid || password !== confirmPassword) return;

    setSaving(true);
    setError('');

    try {
      const result = await setPasswordWithToken(token!, password);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to set password');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-primary)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
        <div style={{ maxWidth: '420px', width: '100%', padding: '40px', background: 'var(--color-bg-white)', borderRadius: '16px', boxShadow: 'var(--shadow-xl)', textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
          <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '8px' }}>Password set successfully</h1>
          <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>Your account is now active. You can log in with your email and password.</p>
          <Link to="/login" style={{ display: 'inline-block', background: 'var(--color-primary)', color: 'white', textDecoration: 'none', padding: '12px 32px', borderRadius: '8px', fontSize: '15px', fontWeight: 600 }}>Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg-primary)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ maxWidth: '420px', width: '100%', padding: '40px', background: 'var(--color-bg-white)', borderRadius: '16px', boxShadow: 'var(--shadow-xl)' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: '4px', textAlign: 'center' }}>Set your password</h1>
        <p style={{ fontSize: '15px', color: 'var(--color-text-secondary)', marginBottom: '28px', textAlign: 'center' }}>Choose a secure password for your account.</p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px', color: 'var(--color-text-primary)' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setValidation(validatePasswordStrength(e.target.value)); }}
              placeholder="Enter password"
              style={{ width: '100%', padding: '10px 14px', fontSize: '15px', border: '2px solid var(--color-border)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px', color: 'var(--color-text-primary)' }}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm password"
              style={{ width: '100%', padding: '10px 14px', fontSize: '15px', border: `2px solid ${confirmPassword && password !== confirmPassword ? 'var(--color-danger)' : 'var(--color-border)'}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }}
            />
            {confirmPassword && password !== confirmPassword && (
              <p style={{ fontSize: '13px', color: 'var(--color-danger)', marginTop: '4px' }}>Passwords do not match</p>
            )}
          </div>

          {password && (
            <div style={{ padding: '12px 14px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '13px', color: 'var(--color-text-primary)', margin: '0 0 8px 0', fontWeight: 600 }}>Password Requirements</p>
              <div className="grid-2col" style={{ gap: '6px', fontSize: '13px' }}>
                {PASSWORD_CHECKS.map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {validation[key] ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="3"><circle cx="12" cy="12" r="10" /></svg>
                    )}
                    <span style={{ color: validation[key] ? 'var(--color-success)' : 'var(--color-text-muted)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--color-danger-light)', border: '1px solid var(--color-danger-border)', borderRadius: '8px', marginBottom: '16px', fontSize: '14px', color: 'var(--color-danger)' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={saving || !allValid || password !== confirmPassword}
            style={{
              width: '100%', padding: '12px', fontSize: '15px', fontWeight: 600,
              background: saving || !allValid || password !== confirmPassword ? 'var(--color-secondary)' : 'var(--color-primary)',
              color: 'white', border: 'none', borderRadius: '8px',
              cursor: saving || !allValid || password !== confirmPassword ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Setting password...' : 'Set Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
